import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Loading, TournamentSelector, AdminCalendar } from "../components";
import { ImageWithFallback } from "../components/match";
import { useNavContext } from "../context/nav_context";
import {
  FiPlus,
  FiSettings,
  FiAward,
  FiUsers,
  FiLayers,
  FiCalendar,
  FiEdit,
  FiShield,
  FiChevronDown,
  FiShuffle,
  FiGitMerge,
} from "react-icons/fi";
import axios from "axios";
import {
  api_v3_list_of_tournaments,
  api_v1_get_current_tournament,
  api_v1_update_current_tournament,
  api_v3_update_tournament_by_id,
  api_v3_create_tournament_by_id,
  api_v3_delete_tournament_by_id,
  api_v3_get_classification_strategies,
  api_v3_refresh_all_classifications,
  randomAvatar,
} from "../utils/constants";

const AdminHome = () => {
  const { user } = useNavContext();
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState({
    id: "",
    name: "",
  });
  const [activeTab, setActiveTab] = useState("panel");
  const [activeTournament, setActiveTournament] = useState(null);
  const [loadState, setLoadState] = useState({ isLoading: false, error: null });
  const [newTournament, setNewTournament] = useState({
    name: "", link: "", type: "", status: "private", startDate: "", endDate: "", classificationStrategy: "LEA_V1"
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [classificationStrategies, setClassificationStrategies] = useState([]);
  const [refreshTarget, setRefreshTarget] = useState(null);
  const [refreshResult, setRefreshResult] = useState(null);

  const STRATEGY_SUMMARIES = {
    LEA_V1: 'Puntos → Admin Lose → Diferencia de series → Series ganadas → Duelo directo',
    LEA_V2: 'Puntos → Admin Lose → Duelo directo → Diferencia de series → Series ganadas → Diferencia de mapas',
  };

  const funnyPhrases = [
    "Que la flecha del arquero no desvíe tu camino",
    "Mejor inyectar madera que pasar toda la partida sin ella",
    "No olvides domesticar ovejas antes de gestionar torneos",
    "Un admin organizado vale más que mil palizas",
    "Que el eco de tu campamento resuene en cada jornada",
    "Mantén tus aldeanos a salvo y tus torneos también",
    "Hoy es un buen día para arrasar como un campeón",
    "Mejor construir muros que lamentar incursiones",
    "Que el sobrante de tu madera siempre alcance para todo",
    "Los grandes imperios se construyen jornada a jornada",
    "Que la niebla de guerra no te impida ver los errores",
    "Un buen admin conoce sus rutas como un mercader sus caminos",
    "Hora de curar heridas y preparar la próxima batalla",
    "Que el oro nunca falte y los conflictos sean mínimos",
    "Los héroes se forjan en la liga, no en la retirada",
    "Mejor un torneo bien planeado que una conquista apresurada",
    "Que la victoria te sonría y los bugs te ignoren",
    "No subestimes el poder de un buen draft",
    "La edad oscura siempre precede a la edad de oro",
    "Que tus decisiones sean certeras como un monje convirtiendo",
    "Sigue el camino de la semurinha maldito insecto",
  ];
  const [currentPhrase, setCurrentPhrase] = useState(
    () => funnyPhrases[Math.floor(Math.random() * funnyPhrases.length)]
  );
  const [shuffleCount, setShuffleCount] = useState(0);
  const [showScare, setShowScare] = useState(false);
  const shufflePhrase = () => {
    const newCount = shuffleCount + 1;
    setShuffleCount(newCount);
    if (newCount >= 10) {
      setShowScare(true);
      setShuffleCount(0);
      const audio = new Audio("https://www.myinstants.com/media/sounds/aoe2-30-wololo.mp3");
      audio.volume = 1;
      audio.play();
      setTimeout(() => setShowScare(false), 2000);
      return;
    }
    let next;
    do {
      next = funnyPhrases[Math.floor(Math.random() * funnyPhrases.length)];
    } while (next === currentPhrase && funnyPhrases.length > 1);
    setCurrentPhrase(next);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [tRes, aRes, sRes] = await Promise.all([
          axios.get(api_v3_list_of_tournaments()),
          axios.get(api_v1_get_current_tournament()),
          axios.get(api_v3_get_classification_strategies()),
        ]);
        const sorted = tRes.data.sort(
          (a, b) => new Date(b.startDate) - new Date(a.startDate)
        );
        setTournaments(sorted);
        setClassificationStrategies(sRes.data);
        if (sorted.length > 0) {
          setSelectedTournament({
            id: sorted[0].tournamentId,
            name: sorted[0].name,
          });
        }
        if (aRes.data && aRes.data.tournamentId)
          setActiveTournament(aRes.data.tournamentId);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!showCreateDialog) return;
    const handler = (e) => {
      if (e.key === "Escape") setShowCreateDialog(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showCreateDialog]);

  const handleRequest = (method, url, data, callback) => {
    setLoadState({ isLoading: true, error: null });
    axios({ method, url, data })
      .then((response) => {
        setLoadState({ isLoading: false, error: false });
        if (callback) callback(response.data);
      })
      .catch((error) => {
        setLoadState({ isLoading: false, error: true });
        console.error("Error:", error);
      });
  };

  const handleSaveTournament = (id) => {
    const t = tournaments.find((x) => x.tournamentId === id);
    if (!t) return;
    handleRequest(
      "put",
      api_v3_update_tournament_by_id(id),
      { ...t, image: t.image || "" },
      (d) => console.log("Tournament updated:", d)
    );
  };

  const handleActivateTournament = (id) => {
    handleRequest(
      "put",
      api_v1_update_current_tournament(),
      { tournamentId: id },
      () => setActiveTournament(id)
    );
  };

  const handleRefreshClassifications = () => {
    const t = tournaments.find((x) => x.tournamentId === selectedTournament.id);
    if (!t) return;
    setLoadState({ isLoading: true, error: null });
    axios
      .post(api_v3_refresh_all_classifications(selectedTournament.id))
      .then((response) => {
        setLoadState({ isLoading: false, error: false });
        setRefreshResult(response.data);
      })
      .catch(() => {
        setLoadState({ isLoading: false, error: true });
      });
  };

  const updateField = (id, field, value) =>
    setTournaments(
      tournaments.map((t) =>
        t.tournamentId === id ? { ...t, [field]: value } : t
      )
    );

  const updatePeriod = (id, idx, field, value) =>
    setTournaments(
      tournaments.map((t) => {
        if (t.tournamentId !== id) return t;
        const periods = t.periods ? [...t.periods] : [];
        periods[idx] = { ...periods[idx], [field]: value };
        return { ...t, periods };
      })
    );

  const addPeriod = (id, startDate) =>
    setTournaments(
      tournaments.map((t) =>
        t.tournamentId === id
          ? {
              ...t,
              periods: [
                ...(t.periods || []),
                {
                  name: "",
                  startDate: startDate || t.startDate || "",
                  endDate: t.endDate || "",
                  color: "#000000",
                },
              ],
            }
          : t
      )
    );

  const removePeriod = (id, idx) =>
    setTournaments(
      tournaments.map((t) =>
        t.tournamentId === id && t.periods
          ? { ...t, periods: t.periods.filter((_, i) => i !== idx) }
          : t
      )
    );

  const updateMilestone = (id, idx, field, value) =>
    setTournaments(
      tournaments.map((t) => {
        if (t.tournamentId !== id) return t;
        const milestones = t.milestones ? [...t.milestones] : [];
        milestones[idx] = { ...milestones[idx], [field]: value };
        return { ...t, milestones };
      })
    );

  const addMilestone = (id, date) =>
    setTournaments(
      tournaments.map((t) =>
        t.tournamentId === id
          ? {
              ...t,
              milestones: [
                ...(t.milestones || []),
                { name: "", date: date || new Date().toISOString() },
              ],
            }
          : t
      )
    );

  const removeMilestone = (id, idx) =>
    setTournaments(
      tournaments.map((t) =>
        t.tournamentId === id && t.milestones
          ? {
              ...t,
              milestones: t.milestones.filter((_, i) => i !== idx),
            }
          : t
      )
    );

  const handleCreate = () => {
    handleRequest(
      "post",
      api_v3_create_tournament_by_id(),
      {
        ...newTournament,
        tournamentId: "",
        image: "",
        timeCreated: new Date().toISOString(),
        timeUpdated: new Date().toISOString(),
      },
      (created) => {
        setTournaments([...tournaments, created]);
        setShowCreateDialog(false);
        setNewTournament({
          name: "", link: "", type: "", status: "private", startDate: "", endDate: "",
        });
      }
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    handleRequest(
      "delete",
      api_v3_delete_tournament_by_id(deleteTarget.tournamentId),
      null,
      () => {
        setTournaments(
          tournaments.filter(
            (t) => t.tournamentId !== deleteTarget.tournamentId
          )
        );
        if (selectedTournament.id === deleteTarget.tournamentId) {
          const remaining = tournaments.filter(
            (t) => t.tournamentId !== deleteTarget.tournamentId
          );
          setSelectedTournament({
            id: remaining.length > 0 ? remaining[0].tournamentId : "",
            name: remaining.length > 0 ? remaining[0].name : "",
          });
        }
        setDeleteTarget(null);
      }
    );
  };

  if (!user) {
    return (
      <Canvas>
        <EmptyState>Debes iniciar sesión para acceder a este perfil.</EmptyState>
      </Canvas>
    );
  }

  if (!user.isAdmin && !user.isModerator) {
    return (
      <Canvas>
        <EmptyState>
          Lo siento, pero estás intentando acceder a donde no debes.
        </EmptyState>
      </Canvas>
    );
  }

  if (isLoading) return <Canvas><Loading /></Canvas>;

  const tLabel = selectedTournament.name.replace("Temporada ", "T");
  const tournament = tournaments.find(
    (t) => t.tournamentId === selectedTournament.id
  );
  const isAdmin = user.isAdmin;

  const mainActions = [
    {
      icon: <FiUsers />,
      text: `INSCRIPCIONES ${tLabel}`,
      desc: "Gestionar inscripciones de jugadores",
      link: `/admin/registrations/${selectedTournament.id}`,
      adminOnly: true,
    },
    {
      icon: <FiLayers />,
      text: `GRUPOS ${tLabel}`,
      desc: "Gestión drag & drop de grupos",
      link: `/admin/tournament/${selectedTournament.id}/groups-management`,
      adminOnly: true,
    },
    {
      icon: <FiCalendar />,
      text: `PARTIDOS ${tLabel}`,
      desc: "Revisar partidos",
      link: `/admin/tournament/${selectedTournament.id}/matches`,
      adminOnly: false,
    },
    {
      icon: <FiSettings />,
      text: `DETALLES ${tLabel}`,
      desc: "Editar mapas, imagen, descripciones...",
      link: `/admin/tournament/${selectedTournament.id}/details`,
      adminOnly: true,
    },
  ];

  const extraActions = [
    {
      icon: <FiEdit />,
      text: `CREAR PARTIDOS ${tLabel}`,
      desc: "Crear partidos manualmente",
      link: `/admin/tournament/${selectedTournament.id}/matches/create`,
      adminOnly: true,
    },
    {
      icon: <FiAward />,
      text: `HALL OF FAME ${tLabel}`,
      desc: "Gestionar el Hall of Fame",
      link: `/admin/tournament/${selectedTournament.id}/hall-of-fame`,
      adminOnly: true,
    },
  ];

  const visibleMainActions = isAdmin
    ? mainActions
    : mainActions.filter((a) => !a.adminOnly);

  return (
    <Canvas>
      <Content>
        {loadState.isLoading && (
          <Overlay>
            <OverlayBox>
              <p>Guardando cambios...</p>
              <OverlayClose onClick={() => setLoadState({ isLoading: false, error: null })}>
                CERRAR
              </OverlayClose>
            </OverlayBox>
          </Overlay>
        )}
        {loadState.error !== null && !loadState.isLoading && (
          <Overlay>
            <OverlayBox>
              <p>
                {loadState.error
                  ? "Hubo un error al guardar los cambios"
                  : "¡Cambios guardados correctamente!"}
              </p>
              <OverlayClose onClick={() => setLoadState({ isLoading: false, error: null })}>
                CERRAR
              </OverlayClose>
            </OverlayBox>
          </Overlay>
        )}

        <PageTitle>ADMIN PANEL</PageTitle>

        <WelcomeCard>
          <WelcomeAvatar>
            <ImageWithFallback
              src={user.discordAvatarURL || randomAvatar()}
              alt={user.username}
              fallbackSrc={randomAvatar()}
            />
          </WelcomeAvatar>
          <WelcomeText>
            <Greeting>Hola {user.username}</Greeting>
            <PhraseRow>
              <PhraseText>{currentPhrase}</PhraseText>
              <ShuffleBtn onClick={shufflePhrase} title="Otra frase">
                <FiShuffle />
              </ShuffleBtn>
            </PhraseRow>
          </WelcomeText>
        </WelcomeCard>

        {isAdmin && (
          <GeneralSection>
            <GeneralLabel>ADMINISTRACIÓN GENERAL</GeneralLabel>
            <GeneralCard to="/admin/mods">
              <GeneralIcon><FiShield /></GeneralIcon>
              <GeneralText>
                <GeneralTitle>MODERADORES</GeneralTitle>
                <GeneralDesc>Gestionar moderadores de la plataforma</GeneralDesc>
              </GeneralText>
            </GeneralCard>
            {user.isSuperAdmin && (
              <GeneralCard to="/admin/merge-players" style={{ marginTop: "0.5rem" }}>
                <GeneralIcon><FiGitMerge /></GeneralIcon>
                <GeneralText>
                  <GeneralTitle>MERGE JUGADORES</GeneralTitle>
                  <GeneralDesc>Fusionar cuentas duplicadas de un mismo usuario</GeneralDesc>
                </GeneralText>
              </GeneralCard>
            )}
            {user.isSuperAdmin && (
              <GeneralCard to="/admin/users" style={{ marginTop: "0.5rem" }}>
                <GeneralIcon><FiUsers /></GeneralIcon>
                <GeneralText>
                  <GeneralTitle>GESTIÓN USUARIOS</GeneralTitle>
                  <GeneralDesc>Administrar usuarios de la comunidad</GeneralDesc>
                </GeneralText>
              </GeneralCard>
            )}
          </GeneralSection>
        )}

        <SelectorRow>
          <TournamentSelector
            tournaments={tournaments}
            currentTournament={selectedTournament.id}
            setCurrentTournament={(id) => {
              const t = tournaments.find((x) => x.tournamentId === id);
              setSelectedTournament({ id, name: t ? t.name : "" });
            }}
          />
          {isAdmin && (
            <AddPill onClick={() => setShowCreateDialog(true)} title="Crear nuevo torneo">
              <FiPlus />
            </AddPill>
          )}
        </SelectorRow>

        <TabBar>
          <Tab $active={activeTab === "panel"} onClick={() => setActiveTab("panel")}>
            PANEL
          </Tab>
          {isAdmin && (
            <Tab $active={activeTab === "configuracion"} onClick={() => setActiveTab("configuracion")}>
              CONFIGURACIÓN
            </Tab>
          )}
        </TabBar>

        {activeTab === "panel" && (
          <>
            <ActionGrid>
              {visibleMainActions.length > 0 ? (
                visibleMainActions.map((action) => (
                  <ActionCard key={action.text} to={action.link}>
                    <ActionIcon>{action.icon}</ActionIcon>
                    <ActionText>{action.text}</ActionText>
                    <ActionDesc>{action.desc}</ActionDesc>
                  </ActionCard>
                ))
              ) : (
                <EmptyState>No hay acciones disponibles. Selecciona un torneo.</EmptyState>
              )}
            </ActionGrid>

            {isAdmin && extraActions.length > 0 && (
              <CollapsibleSection>
                <CollapseToggle
                  onClick={() => setCollapseOpen(!collapseOpen)}
                  $open={collapseOpen}
                >
                  <CollapseLabel>
                    <CollapseDot /> MÁS OPCIONES
                  </CollapseLabel>
                  <FiChevronDown />
                </CollapseToggle>
                {collapseOpen && (
                  <CollapseContent>
                    <ActionGrid>
                      {extraActions.map((action) => (
                        <ActionCard key={action.text} to={action.link}>
                          <ActionIcon>{action.icon}</ActionIcon>
                          <ActionText>{action.text}</ActionText>
                          <ActionDesc>{action.desc}</ActionDesc>
                        </ActionCard>
                      ))}
                    </ActionGrid>
                  </CollapseContent>
                )}
              </CollapsibleSection>
            )}
          </>
        )}

        {activeTab === "configuracion" && tournament && (
          <>
            <SectionCard>
              <TournamentId>ID: {tournament.tournamentId}</TournamentId>
              <FieldGrid>
                <Field>
                  <FieldLabel>NAME</FieldLabel>
                  <TextInput
                    value={tournament.name || ""}
                    onChange={(e) =>
                      updateField(tournament.tournamentId, "name", e.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>LINK</FieldLabel>
                  <TextInput
                    value={tournament.link || ""}
                    onChange={(e) =>
                      updateField(tournament.tournamentId, "link", e.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>TYPE</FieldLabel>
                  <TextInput
                    value={tournament.type || ""}
                    onChange={(e) =>
                      updateField(tournament.tournamentId, "type", e.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>START DATE</FieldLabel>
                  <TextInput
                    type="datetime-local"
                    value={
                      tournament.startDate
                        ? tournament.startDate.substring(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      updateField(
                        tournament.tournamentId,
                        "startDate",
                        e.target.value
                      )
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>END DATE</FieldLabel>
                  <TextInput
                    type="datetime-local"
                    value={
                      tournament.endDate
                        ? tournament.endDate.substring(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      updateField(
                        tournament.tournamentId,
                        "endDate",
                        e.target.value
                      )
                    }
                  />
                </Field>
                 <Field>
                   <FieldLabel>STATUS</FieldLabel>
                   <SelectInput
                     value={tournament.status || ""}
                     onChange={(e) =>
                       updateField(
                         tournament.tournamentId,
                         "status",
                         e.target.value
                       )
                     }
                   >
                     <option value="private">private</option>
                     <option value="registers">registers</option>
                     <option value="registers-closed">registers-closed</option>
                     <option value="on going">on going</option>
                     <option value="playoffs">playoffs</option>
                     <option value="finished">finished</option>
                   </SelectInput>
                 </Field>
                  <Field>
                    <FieldLabel>CLASSIFICATION STRATEGY</FieldLabel>
                    <SelectInput
                      value={tournament.classificationStrategy || "LEA_V1"}
                      onChange={(e) =>
                        updateField(
                          tournament.tournamentId,
                          "classificationStrategy",
                          e.target.value
                        )
                      }
                    >
                      {classificationStrategies.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.name}
                        </option>
                      ))}
                    </SelectInput>
                    <FieldDescription>
                      {STRATEGY_SUMMARIES[tournament.classificationStrategy || "LEA_V1"]}
                    </FieldDescription>
                  </Field>
              </FieldGrid>

              <SubSection>
                <SubSectionHeader>
                  <SubSectionTitle>PERIODOS & HITOS</SubSectionTitle>
                </SubSectionHeader>
                <AdminCalendar
                  periods={tournament.periods || []}
                  milestones={tournament.milestones || []}
                  onAddPeriod={(startDate) =>
                    addPeriod(tournament.tournamentId, startDate)
                  }
                  onUpdatePeriod={(idx, field, val) =>
                    updatePeriod(tournament.tournamentId, idx, field, val)
                  }
                  onRemovePeriod={(idx) =>
                    removePeriod(tournament.tournamentId, idx)
                  }
                  onAddMilestone={(date) =>
                    addMilestone(tournament.tournamentId, date)
                  }
                  onUpdateMilestone={(idx, field, val) =>
                    updateMilestone(tournament.tournamentId, idx, field, val)
                  }
                  onRemoveMilestone={(idx) =>
                    removeMilestone(tournament.tournamentId, idx)
                  }
                />
              </SubSection>

              <RefreshSection>
                <RefreshBtn onClick={() => setRefreshTarget(tournament)}>
                  RECALCULAR CLASIFICACIONES
                </RefreshBtn>
              </RefreshSection>

              <ActionsRow>
                <ActivateBtn
                  $active={activeTournament === tournament.tournamentId}
                  onClick={() =>
                    handleActivateTournament(tournament.tournamentId)
                  }
                  disabled={activeTournament === tournament.tournamentId}
                >
                  {activeTournament === tournament.tournamentId
                    ? "TORNEO ACTIVO"
                    : "ACTIVAR TORNEO"}
                </ActivateBtn>
                <SaveBtn
                  onClick={() => handleSaveTournament(tournament.tournamentId)}
                >
                  GUARDAR CAMBIOS
                </SaveBtn>
                <DeleteBtn onClick={() => setDeleteTarget(tournament)}>
                  BORRAR TORNEO
                </DeleteBtn>
              </ActionsRow>
            </SectionCard>

          </>
        )}

        {deleteTarget && (
          <Overlay>
            <ConfirmBox>
              <ConfirmIcon>⚠</ConfirmIcon>
              <ConfirmTitle>¿BORRAR TORNEO?</ConfirmTitle>
              <ConfirmText>
                Esta acción es <strong>irreversible</strong>. Se eliminarán
                permanentemente el torneo{" "}
                <strong>"{deleteTarget.name}"</strong> y todos sus datos
                asociados (divisiones, partidos, inscripciones...).
              </ConfirmText>
              <ConfirmActions>
                <CancelBtn onClick={() => setDeleteTarget(null)}>
                  CANCELAR
                </CancelBtn>
                <DangerBtn onClick={confirmDelete}>
                  CONFIRMAR BORRADO
                </DangerBtn>
              </ConfirmActions>
            </ConfirmBox>
          </Overlay>
        )}

        {refreshTarget && (
          <Overlay>
            <ConfirmBox>
              <ConfirmTitle>RECALCULAR CLASIFICACIONES</ConfirmTitle>
              <ConfirmText>
                Se van a recalcular las clasificaciones de <strong>todos los grupos</strong> del torneo <strong>"{refreshTarget.name}"</strong>
                usando la estrategia actual ({refreshTarget.classificationStrategy || "LEA_V1"}).
                {refreshTarget.classificationStrategy === "LEA_V2" && (
                  <><br /><br />Asegúrate de que los partidos tienen correctamente asignados los AW/AL antes de recalcular.</>
                )}
              </ConfirmText>
              <ConfirmActions>
                <CancelBtn onClick={() => setRefreshTarget(null)}>
                  CANCELAR
                </CancelBtn>
                <SaveBtn onClick={() => { setRefreshTarget(null); handleRefreshClassifications(); }}>
                  RECALCULAR
                </SaveBtn>
              </ConfirmActions>
            </ConfirmBox>
          </Overlay>
        )}

        {refreshResult && (
          <Overlay onClick={() => setRefreshResult(null)}>
            <OverlayBox>
              <p>
                Clasificaciones recalculadas correctamente:
                {refreshResult.refreshedGroups} grupos actualizados.
              </p>
              <OverlayClose onClick={() => setRefreshResult(null)}>
                CERRAR
              </OverlayClose>
            </OverlayBox>
          </Overlay>
        )}

        {showCreateDialog && (
          <Overlay onClick={() => setShowCreateDialog(false)}>
            <CreateDialog onClick={(e) => e.stopPropagation()}>
              <CreateDialogTitle>CREAR NUEVO TORNEO</CreateDialogTitle>
              <FieldGrid>
                <Field>
                  <FieldLabel>NAME</FieldLabel>
                  <TextInput
                    value={newTournament.name}
                    onChange={(e) =>
                      setNewTournament({ ...newTournament, name: e.target.value })
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>LINK</FieldLabel>
                  <TextInput
                    value={newTournament.link}
                    onChange={(e) =>
                      setNewTournament({ ...newTournament, link: e.target.value })
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>TYPE</FieldLabel>
                  <TextInput
                    value={newTournament.type}
                    onChange={(e) =>
                      setNewTournament({ ...newTournament, type: e.target.value })
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>START DATE</FieldLabel>
                  <TextInput
                    type="datetime-local"
                    value={newTournament.startDate}
                    onChange={(e) =>
                      setNewTournament({ ...newTournament, startDate: e.target.value })
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>END DATE</FieldLabel>
                  <TextInput
                    type="datetime-local"
                    value={newTournament.endDate}
                    onChange={(e) =>
                      setNewTournament({ ...newTournament, endDate: e.target.value })
                    }
                  />
                </Field>
                 <Field>
                   <FieldLabel>STATUS</FieldLabel>
                   <SelectInput
                     value={newTournament.status}
                     onChange={(e) =>
                       setNewTournament({ ...newTournament, status: e.target.value })
                     }
                   >
                     <option value="private">Private</option>
                     <option value="registers">Registers</option>
                     <option value="on going">On Going</option>
                     <option value="finished">Finished</option>
                   </SelectInput>
                 </Field>
                  <Field>
                    <FieldLabel>CLASSIFICATION STRATEGY</FieldLabel>
                    <SelectInput
                      value={newTournament.classificationStrategy}
                      onChange={(e) =>
                        setNewTournament({ ...newTournament, classificationStrategy: e.target.value })
                      }
                    >
                      {classificationStrategies.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.name}
                        </option>
                      ))}
                    </SelectInput>
                    <FieldDescription>
                      {STRATEGY_SUMMARIES[newTournament.classificationStrategy]}
                    </FieldDescription>
                  </Field>
              </FieldGrid>
              <CreateDialogActions>
                <CancelBtn onClick={() => setShowCreateDialog(false)}>CANCELAR</CancelBtn>
                <SaveBtn onClick={handleCreate}>CREAR TORNEO</SaveBtn>
              </CreateDialogActions>
            </CreateDialog>
          </Overlay>
        )}

        {showScare && (
          <ScareOverlay $visible={showScare}>
            <ScareImage
              src="https://media1.tenor.com/m/lw73j16fHwQAAAAd/wololo-age-of-empires.gif"
              alt="WOLOLO"
            />
          </ScareOverlay>
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`
  background: var(--v-canvas);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: 4rem 1.5rem;
`;

const Content = styled.div`
  width: 100%;
  max-width: 900px;
`;

const PageTitle = styled.h1`
  font-family: Anton, sans-serif;
  font-size: clamp(48px, 8vw, 72px);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1;
  color: var(--v-fg);
  margin: 0 0 2rem;
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-fg-muted);
  text-align: center;
  padding: 4rem 1rem;
`;

const WelcomeCard = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const WelcomeAvatar = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const WelcomeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Greeting = styled.p`
  font-family: var(--v-font-body);
  font-size: 16px;
  font-weight: 600;
  color: var(--v-fg);
  margin: 0 0 4px;
`;

const PhraseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PhraseText = styled.span`
  font-family: var(--v-font-body);
  font-size: 12px;
  font-style: italic;
  color: var(--v-fg-muted);
  line-height: 1.4;
`;

const ShuffleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  border: 1px solid rgba(60, 255, 208, 0.25);
  background: rgba(60, 255, 208, 0.08);
  color: var(--v-mint);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.15s ease, border-color 0.15s ease;
  flex-shrink: 0;
  font-size: 13px;

  &:hover {
    background: rgba(60, 255, 208, 0.18);
    border-color: var(--v-mint);
  }

  &:active {
    background: rgba(60, 255, 208, 0.25);
  }
`;

const GeneralSection = styled.div`
  margin-bottom: 1.5rem;
`;

const GeneralLabel = styled.p`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-fg-muted);
  margin: 0 0 0.5rem;
`;

const GeneralCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 0.85rem 1.15rem;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(60, 255, 208, 0.12);
    background: rgba(60, 255, 208, 0.02);
  }
`;

const GeneralIcon = styled.div`
  font-size: 1.25rem;
  color: var(--v-fg-muted);
  display: flex;
`;

const GeneralText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GeneralTitle = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg);
`;

const GeneralDesc = styled.span`
  font-family: var(--v-font-body);
  font-size: 12px;
  color: var(--v-fg-muted);
`;

const CollapsibleSection = styled.div`
  margin-top: 1rem;
`;

const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 0.7rem 1rem;
  cursor: pointer;
  color: var(--v-fg-muted);
  font-size: 12px;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: rgba(60, 255, 208, 0.12);
    color: var(--v-fg);
  }

  svg {
    transition: transform 0.2s ease;
    transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
  }
`;

const CollapseLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CollapseDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--v-fg-muted);
`;

const CollapseContent = styled.div`
  margin-top: 0.75rem;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1.5rem;
`;

const AddPill = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1.5px dashed var(--v-mint);
  background: rgba(60, 255, 208, 0.06);
  color: var(--v-mint);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  font-size: 15px;
  line-height: 1;

  &:hover {
    background: rgba(60, 255, 208, 0.14);
    border-style: solid;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 1.5rem;
`;

const Tab = styled.div`
  padding: 0.5rem 1.25rem;
  border: 1px solid ${({ $active }) => ($active ? "var(--v-mint)" : "rgba(255, 255, 255, 0.08)")};
  background: ${({ $active }) => ($active ? "rgba(60, 255, 208, 0.08)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--v-mint)" : "var(--v-fg)")};
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--v-mint);
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.75rem;
  margin-top: 0;
`;

const ActionCard = styled(Link)`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(60, 255, 208, 0.12);
    background: rgba(60, 255, 208, 0.02);
  }
`;

const ActionIcon = styled.div`
  font-size: 1.5rem;
  color: var(--v-fg-muted);
  margin-bottom: 0.25rem;
`;

const ActionText = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg);
  line-height: 1.3;
`;

const ActionDesc = styled.span`
  font-family: var(--v-font-body);
  font-size: 12px;
  color: var(--v-fg-muted);
`;

/* --- Config tab styles --- */

const SectionCard = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
`;

const TournamentId = styled.p`
  font-family: var(--v-font-mono);
  font-size: 10px;
  color: var(--v-fg-muted);
  margin: 0 0 1rem;
  font-style: italic;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div``;

const FieldLabel = styled.label`
  display: block;
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  margin-bottom: 0.3rem;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease;
  box-sizing: border-box;
  &:focus {
    border-color: var(--v-mint);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: var(--v-mint);
  }
  option {
    background: #1a1a1a;
  }
`;

const FieldDescription = styled.div`
  margin-top: 0.35rem;
  font-family: var(--v-font-mono);
  font-size: 9px;
  letter-spacing: 0.3px;
  color: var(--v-fg-muted);
  line-height: 1.4;
`;

const SubSection = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
`;

const SubSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const SubSectionTitle = styled.h4`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  margin: 0;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const SaveBtn = styled.button`
  background: var(--v-mint);
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.85;
  }
`;

const ActivateBtn = styled.button`
  background: ${({ $active }) =>
    $active ? "rgba(60,255,208,0.08)" : "transparent"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--v-mint)" : "rgba(255,255,255,0.15)"};
  color: ${({ $active }) =>
    $active ? "var(--v-mint)" : "var(--v-fg-muted)"};
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const OverlayBox = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  p {
    font-family: var(--v-font-body);
    font-size: 14px;
    color: var(--v-fg);
    margin: 0 0 1rem;
  }
`;

const OverlayClose = styled.button`
  background: var(--v-mint);
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.45rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
`;

const CreateDialog = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 24px;
  padding: 2rem;
  width: 90%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
`;

const CreateDialogTitle = styled.h3`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-fg);
  margin: 0 0 1.25rem;
`;

const CreateDialogActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const DeleteBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 77, 77, 0.3);
  color: #ff4d4d;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 77, 77, 0.08);
    border-color: #ff4d4d;
  }
`;

const ConfirmBox = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 77, 77, 0.2);
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
  max-width: 440px;
  width: 90%;
`;

const ConfirmIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  line-height: 1;
`;

const ConfirmTitle = styled.h3`
  font-family: var(--v-font-display);
  font-size: 28px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #ff4d4d;
  margin: 0 0 1rem;
`;

const ConfirmText = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  line-height: 1.6;
  color: var(--v-secondary);
  margin: 0 0 2rem;

  strong {
    color: var(--v-white);
  }
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`;

const CancelBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--v-white);
    color: var(--v-white);
  }
`;

const DangerBtn = styled.button`
  background: #ff4d4d;
  border: none;
  color: #fff;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
`;

const RefreshSection = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  justify-content: center;
`;

const RefreshBtn = styled.button`
  background: rgba(60, 255, 208, 0.08);
  border: 1px solid rgba(60, 255, 208, 0.3);
  color: var(--v-mint);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.6rem 1.5rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(60, 255, 208, 0.18);
    border-color: var(--v-mint);
  }
`;

const ScareOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  animation: scareFadeIn 0.15s ease-out;

  @keyframes scareFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ScareImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 12px;
`;

export default AdminHome;
